import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useCompareListserv, type ListservComparison } from "@/features/admin/admin";
import { sectionTitleClass } from "@/lib/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";

/**
 * The Google Group's member export, set beside the site's members.
 *
 * The group has no API, so nobody can ask it who is on it: an owner exports
 * the member list and uploads it here. This was a script run from Minima's
 * side (Sami asked for its output on 2026-09-29), including the check, done by
 * eye, for people who are on the group under a different address from the one
 * the site knows. The file is read in the browser and sent as text; nothing
 * of it is kept.
 */
export function ListservCompare() {
  const compare = useCompareListserv();
  const input = useRef<HTMLInputElement>(null);
  const [exported, setExported] = useState("");
  const [result, setResult] = useState<ListservComparison | null>(null);

  function run(text: string, record = false) {
    compare.mutate(
      { export: text, record },
      {
        onSuccess: (data) => {
          setResult(data);
          if (record) toast.success(`${data.recorded} marked as on the listserv`);
        },
      }
    );
  }

  function chosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    // FileReader rather than `file.text()`, which older browsers lack.
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? "");
      setExported(text);
      setResult(null);
      run(text);
    };
    reader.readAsText(file);
  }

  const copy = (addresses: string[]) => {
    navigator.clipboard?.writeText(addresses.join(", "));
    toast.success(`${addresses.length} addresses copied`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Compare with the Google Group</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p>
          Export the member list from the Google Group and upload it to see who is on the
          group with no account here, and which members are not on the group. The file is
          not kept.
        </p>
        <input
          ref={input}
          type="file"
          accept=".csv,text/csv,text/plain"
          aria-label="Google Group member export"
          className="sr-only"
          onChange={chosen}
        />
        <Button
          variant="outline"
          disabled={compare.isPending}
          onClick={() => input.current?.click()}
        >
          {compare.isPending && <Spinner data-slot="icon" />}
          Upload export
        </Button>
        {compare.error && <p className="text-destructive">{compare.error.message}</p>}

        {result && (
          <div className="space-y-6">
            <p>{result.onGroup} addresses are on the group.</p>

            {result.foundUnrecorded > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <p>
                  {result.foundUnrecorded} members are on the group and not yet marked as on
                  the listserv.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={compare.isPending}
                  onClick={() => run(exported, true)}
                >
                  Mark them
                </Button>
              </div>
            )}

            <section className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-medium">
                  On the group with no account ({result.unknown.length})
                </h3>
                {result.unknown.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copy(result.unknown.map((row) => row.email))}
                  >
                    Copy addresses
                  </Button>
                )}
              </div>
              <ul className="max-h-72 space-y-1 overflow-y-auto">
                {result.unknown.map((row) => (
                  <li key={row.email}>
                    {row.email}
                    {row.possible.map((who) => (
                      <span key={who.id} className="text-muted-foreground">
                        {" "}
                        may be{" "}
                        <Link
                          to={`/admin/members/${who.id}`}
                          className="text-foreground underline underline-offset-4"
                        >
                          {who.name}
                        </Link>{" "}
                        ({who.why})
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </section>

            <section className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-medium">
                  Members not on the group ({result.missing.length})
                </h3>
                {result.missing.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copy(result.missing.map((row) => row.address))}
                  >
                    Copy addresses
                  </Button>
                )}
              </div>
              <ul className="max-h-72 space-y-1 overflow-y-auto">
                {result.missing.map((row) => (
                  <li key={row.id}>
                    <Link
                      to={`/admin/members/${row.id}`}
                      className="underline underline-offset-4"
                    >
                      {row.name}
                    </Link>{" "}
                    <span className="text-muted-foreground">
                      {row.address}
                      {row.recorded ? ", marked as on the listserv" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
